import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

// --- Colour system ------------------------------------------------------------
const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8',
  orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF',
  cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
  olive:'#6B7045', oliveLight:'#F0F0E8',
};

// --- Allergen definitions -----------------------------------------------------
const ALLERGENS = [
  { key: 'gluten',       label: 'Gluten',      group: 'main' },
  { key: 'eggs',         label: 'Eggs',         group: 'main' },
  { key: 'fish',         label: 'Fish',         group: 'main' },
  { key: 'crustaceans',  label: 'Crustaceans',  group: 'main' },
  { key: 'molluscs',     label: 'Molluscs',     group: 'main' },
  { key: 'milk_lactose', label: 'Lactose',      group: 'main' },
  { key: 'soy',          label: 'Soy',          group: 'main' },
  { key: 'peanuts',      label: 'Peanuts',      group: 'main' },
  { key: 'celery',       label: 'Celery',       group: 'main' },
  { key: 'mustard',      label: 'Mustard',      group: 'main' },
  { key: 'sesame',       label: 'Sesame',       group: 'main' },
  { key: 'sulphites',    label: 'Sulphites',    group: 'main' },
  { key: 'lupin',        label: 'Lupin',        group: 'main' },
  { key: 'almond',       label: 'Almond',       group: 'nut'  },
  { key: 'hazelnut',     label: 'Hazelnut',     group: 'nut'  },
  { key: 'walnut',       label: 'Walnut',       group: 'nut'  },
  { key: 'cashew',       label: 'Cashew',       group: 'nut'  },
  { key: 'pecan',        label: 'Pecan',        group: 'nut'  },
  { key: 'pistachio',    label: 'Pistachio',    group: 'nut'  },
  { key: 'brazil_nut',   label: 'Brazil',       group: 'nut'  },
  { key: 'macadamia',    label: 'Macadamia',    group: 'nut'  },
  { key: 'pine_nut',     label: 'Pine Nut',     group: 'nut'  },
  { key: 'chestnut',     label: 'Chestnut',     group: 'nut'  },
  { key: 'hickory',      label: 'Hickory',      group: 'nut'  },
];

const MENUS        = ['Brunch', 'Dinner', 'Sassys'];
const DIETARY_OPTS = ['None', 'Vegetarian', 'Vegan', 'Vegetarian option', 'Vegan option'];

// --- Status cycle: no -> yes -> may -> sub -> rem -> no ---------------------------
function cycleStatus(current) {
  if (current === 'no')  return 'yes';
  if (current === 'yes') return 'may';
  if (current === 'may') return 'sub';
  if (current === 'sub') return 'rem';
  return 'no';
}

// --- Status icon --------------------------------------------------------------
function StatusIcon({ value, small = false }) {
  const size  = small ? 20 : 24;
  const fsize = small ? '0.72rem' : '0.85rem';

  if (value === 'yes') return (
    <span title="Contains" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%',
      background: C.redLight, color: C.red,
      fontSize: fsize, fontWeight: 700,
    }}>X</span>
  );
  if (value === 'may') return (
    <span title="May contain / traces" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%',
      background: C.amberLight, color: C.amber,
      fontSize: fsize,
    }}>~</span>
  );
  if (value === 'sub') return (
    <span title="Contains - can be subbed (may cost extra)" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%',
      background: C.skyBlueLight, color: C.skyText,
      fontSize: fsize, fontWeight: 700,
    }}>
      Xs
    </span>
  );
  if (value === 'rem') return (
    <span title="Contains - can be removed" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, borderRadius: '50%',
      background: C.oliveLight, color: C.olive,
      fontSize: fsize, fontWeight: 700,
    }}>
      Xr
    </span>
  );
  return (
    <span title="Does Not Contain" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, color: C.border,
      fontSize: small ? '0.85rem' : '1rem', lineHeight: 1,
    }}>-</span>
  );
}

// --- Dietary badge ------------------------------------------------------------
function dietaryBadge(dietary) {
  if (dietary === 'Vegan')             return { bg: C.greenLight,   color: C.green,   label: 'VG'  };
  if (dietary === 'Vegetarian')        return { bg: C.skyBlueLight, color: C.skyText, label: 'V'   };
  if (dietary === 'Vegan option')      return { bg: C.greenLight,   color: C.green,   label: 'VG*' };
  if (dietary === 'Vegetarian option') return { bg: C.skyBlueLight, color: C.skyText, label: 'V*'  };
  return null;
}

const emptyForm = {
  name: '', menu: 'Brunch', dietary: 'None', active: true, notes: '',
  ...Object.fromEntries(ALLERGENS.map(a => [a.key, 'no'])),
};

// --- Allergen status button style helper --------------------------------------
function statusButtonStyle(val) {
  if (val === 'yes') return { border: `1px solid ${C.red}`,     background: C.redLight,     color: C.charcoal };
  if (val === 'may') return { border: `1px solid ${C.amber}`,   background: C.amberLight,   color: C.charcoal };
  if (val === 'sub') return { border: `1px solid ${C.skyText}`, background: C.skyBlueLight, color: C.charcoal };
  if (val === 'rem') return { border: `1px solid ${C.olive}`,   background: C.oliveLight,   color: C.charcoal };
  return               { border: `1px solid ${C.border}`,       background: C.cream,        color: C.charcoal };
}

// --- Dish detail / edit modal -------------------------------------------------
function DishModal({ dish, isAdmin, onClose, onSaved }) {
  const [editMode,   setEditMode]   = useState(false);
  const [formData,   setFormData]   = useState({ ...emptyForm, ...dish });
  const [confirming, setConfirming] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [showNuts,   setShowNuts]   = useState(false);

  const isDirty = JSON.stringify(formData) !== JSON.stringify({ ...emptyForm, ...dish });

  const presentAllergens = ALLERGENS.filter(a => {
    const v = dish[a.key];
    return v === 'yes' || v === 'may' || v === 'sub' || v === 'rem';
  });

  function handleEditClick() {
    setFormData({ ...emptyForm, ...dish });
    setEditMode(true);
    setError(null);
  }

  function handleCancelEdit() {
    if (isDirty) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    setEditMode(false);
    setFormData({ ...emptyForm, ...dish });
    setConfirming(false);
    setError(null);
  }

  function handleSaveClick() {
    if (!formData.name) return;
    setConfirming(true);
  }

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    const payload = {
      name: formData.name, menu: formData.menu,
      dietary: formData.dietary, active: formData.active,
      notes: formData.notes || '',
      ...Object.fromEntries(ALLERGENS.map(a => [a.key, formData[a.key] || 'no'])),
    };
    const { error: err } = await supabase.from('allergen_list').update(payload).eq('id', dish.id);
    if (err) { setError('Save failed: ' + err.message); setSaving(false); setConfirming(false); return; }
    onSaved({ ...dish, ...payload });
    setSaving(false);
    setConfirming(false);
    setEditMode(false);
  }

  const badge = dietaryBadge(dish.dietary);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) { if (editMode && isDirty) { if (!window.confirm('Discard unsaved changes?')) return; } onClose(); }}}
      style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0' }}
    >
      <div style={{ background: C.white, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '600px', maxHeight: '92vh', overflowY: 'auto', padding: '1.5rem 1.25rem 2rem', boxShadow: '0 -4px 32px rgba(0,0,0,0.18)' }}>

        <div style={{ width: 40, height: 4, borderRadius: 2, background: C.border, margin: '0 auto 1.25rem' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: C.charcoal }}>
                {editMode ? formData.name : dish.name}
              </h2>
              {badge && !editMode && (
                <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: '999px', background: badge.bg, color: badge.color }}>
                  {badge.label}
                </span>
              )}
            </div>
            <span style={{ fontSize: '0.75rem', color: C.mid, marginTop: '0.2rem', display: 'block' }}>
              {editMode ? formData.menu : dish.menu}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', color: C.mid, cursor: 'pointer', padding: '0.2rem 0.4rem', lineHeight: 1 }}>X</button>
        </div>

        {error && (
          <div style={{ background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '0.6rem 0.85rem', marginBottom: '0.85rem', color: C.red, fontSize: '0.82rem' }}>
            {error}
          </div>
        )}

        {/* -- READ-ONLY VIEW -- */}
        {!editMode && (
          <>
            {presentAllergens.length === 0 ? (
              <div style={{ color: C.mid, fontSize: '0.9rem', padding: '1rem 0' }}>No allergens recorded for this dish.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                {presentAllergens.map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.55rem 0.85rem', borderRadius: '10px', background: C.cream, border: `1px solid ${C.border}` }}>
                    <StatusIcon value={dish[key]} />
                    <span style={{ fontSize: '0.92rem', fontWeight: 600, color: C.charcoal }}>{label}</span>
                    <span style={{ fontSize: '0.75rem', color: C.mid, marginLeft: 'auto' }}>
                      {dish[key] === 'yes' && 'Contains'}
                      {dish[key] === 'may' && 'May contain'}
                      {dish[key] === 'sub' && 'Can be subbed'}
                      {dish[key] === 'rem' && 'Can be removed'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {dish.notes && (
              <div style={{ background: C.yellowLight, border: `1px solid ${C.yellow}`, borderRadius: '10px', padding: '0.65rem 0.85rem', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.olive, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>Notes</div>
                <div style={{ fontSize: '0.88rem', color: C.charcoal }}>{dish.notes}</div>
              </div>
            )}

            {isAdmin && (
              <button onClick={handleEditClick} style={{ width: '100%', padding: '0.65rem', background: C.navy, color: C.white, border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
                Edit Dish
              </button>
            )}
          </>
        )}

        {/* -- EDIT VIEW -- */}
        {editMode && (
          <>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dish Name</label>
              <input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: `1.5px solid ${formData.name ? C.navy : C.border}`, borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', color: C.charcoal, outline: 'none' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Menu</label>
                <select value={formData.menu} onChange={e => setFormData(p => ({ ...p, menu: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal }}>
                  {MENUS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dietary</label>
                <select value={formData.dietary} onChange={e => setFormData(p => ({ ...p, dietary: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal }}>
                  {DIETARY_OPTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              EU Allergens - tap to cycle
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.4rem', marginBottom: '0.75rem' }}>
              {ALLERGENS.filter(a => a.group === 'main').map(({ key, label }) => (
                <button key={key} onClick={() => setFormData(p => ({ ...p, [key]: cycleStatus(p[key]) }))}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.42rem 0.65rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.84rem', ...statusButtonStyle(formData[key]) }}>
                  <span>{label}</span>
                  <StatusIcon value={formData[key]} small />
                </button>
              ))}
            </div>

            <button onClick={() => setShowNuts(p => !p)}
              style={{ marginBottom: '0.5rem', padding: '0.3rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${showNuts ? C.orange : C.border}`, background: showNuts ? C.orangeLight : C.white, color: showNuts ? C.orange : C.mid, fontSize: '0.8rem', cursor: 'pointer', fontWeight: showNuts ? 600 : 400 }}>
              {showNuts ? 'Hide tree nuts' : 'Show tree nuts'}
            </button>
            {showNuts && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.4rem', marginBottom: '0.75rem' }}>
                {ALLERGENS.filter(a => a.group === 'nut').map(({ key, label }) => (
                  <button key={key} onClick={() => setFormData(p => ({ ...p, [key]: cycleStatus(p[key]) }))}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.42rem 0.65rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.84rem', ...statusButtonStyle(formData[key]) }}>
                    <span>{label}</span>
                    <StatusIcon value={formData[key]} small />
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</label>
              <textarea value={formData.notes || ''} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g. Ask kitchen about cross-contamination risk on sesame"
                rows={3}
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box', color: C.charcoal, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer', color: C.charcoal, marginBottom: '1.25rem' }}>
              <input type="checkbox" checked={formData.active} onChange={e => setFormData(p => ({ ...p, active: e.target.checked }))} />
              Active (visible to staff)
            </label>

            {!confirming ? (
              <div style={{ display: 'flex', gap: '0.65rem' }}>
                <button onClick={handleSaveClick} disabled={!formData.name || saving}
                  style={{ flex: 1, padding: '0.65rem', background: !formData.name ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem', cursor: !formData.name ? 'not-allowed' : 'pointer' }}>
                  Save Changes
                </button>
                <button onClick={handleCancelEdit}
                  style={{ padding: '0.65rem 1.1rem', border: `1px solid ${C.border}`, borderRadius: '10px', background: C.white, cursor: 'pointer', color: C.mid, fontSize: '0.88rem' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ background: C.amberLight, border: `1px solid ${C.amber}`, borderRadius: '12px', padding: '1rem', marginTop: '0.5rem' }}>
                <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: C.charcoal, fontSize: '0.9rem' }}>
                  Update <strong>{formData.name}</strong>? This will save to the database.
                </p>
                <div style={{ display: 'flex', gap: '0.65rem' }}>
                  <button onClick={handleConfirm} disabled={saving}
                    style={{ flex: 1, padding: '0.6rem', background: C.amber, color: C.white, border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                    {saving ? 'Saving...' : 'Confirm Update'}
                  </button>
                  <button onClick={() => setConfirming(false)}
                    style={{ padding: '0.6rem 1rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', color: C.mid, fontSize: '0.88rem' }}>
                    Go Back
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- Add Dish Modal -----------------------------------------------------------
function AddDishModal({ allowedMenus, onClose, onSaved }) {
  const [formData,   setFormData]   = useState({ ...emptyForm, menu: allowedMenus[0] });
  const [confirming, setConfirming] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [showNuts,   setShowNuts]   = useState(false);

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    const payload = {
      name: formData.name, menu: formData.menu,
      dietary: formData.dietary, active: formData.active,
      notes: formData.notes || '',
      ...Object.fromEntries(ALLERGENS.map(a => [a.key, formData[a.key] || 'no'])),
    };
    const { data, error: err } = await supabase.from('allergen_list').insert(payload).select().single();
    if (err) { setError('Save failed: ' + err.message); setSaving(false); setConfirming(false); return; }
    onSaved(data);
    setSaving(false);
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: C.white, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '600px', maxHeight: '92vh', overflowY: 'auto', padding: '1.5rem 1.25rem 2rem', boxShadow: '0 -4px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: C.border, margin: '0 auto 1.25rem' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: C.charcoal }}>New Dish</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', color: C.mid, cursor: 'pointer', padding: '0.2rem 0.4rem', lineHeight: 1 }}>X</button>
        </div>

        {error && (
          <div style={{ background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '0.6rem 0.85rem', marginBottom: '0.85rem', color: C.red, fontSize: '0.82rem' }}>{error}</div>
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dish Name</label>
          <input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Eggs Benny & The Jets"
            style={{ width: '100%', padding: '0.5rem 0.7rem', border: `1.5px solid ${formData.name ? C.navy : C.border}`, borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', color: C.charcoal, outline: 'none' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Menu</label>
            <select value={formData.menu} onChange={e => setFormData(p => ({ ...p, menu: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal }}>
              {allowedMenus.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dietary</label>
            <select value={formData.dietary} onChange={e => setFormData(p => ({ ...p, dietary: e.target.value }))}
              style={{ width: '100%', padding: '0.5rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal }}>
              {DIETARY_OPTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          EU Allergens - tap to cycle
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.4rem', marginBottom: '0.75rem' }}>
          {ALLERGENS.filter(a => a.group === 'main').map(({ key, label }) => (
            <button key={key} onClick={() => setFormData(p => ({ ...p, [key]: cycleStatus(p[key]) }))}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.42rem 0.65rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.84rem', ...statusButtonStyle(formData[key]) }}>
              <span>{label}</span>
              <StatusIcon value={formData[key]} small />
            </button>
          ))}
        </div>

        <button onClick={() => setShowNuts(p => !p)}
          style={{ marginBottom: '0.5rem', padding: '0.3rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${showNuts ? C.orange : C.border}`, background: showNuts ? C.orangeLight : C.white, color: showNuts ? C.orange : C.mid, fontSize: '0.8rem', cursor: 'pointer', fontWeight: showNuts ? 600 : 400 }}>
          {showNuts ? 'Hide tree nuts' : 'Show tree nuts'}
        </button>
        {showNuts && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.4rem', marginBottom: '0.75rem' }}>
            {ALLERGENS.filter(a => a.group === 'nut').map(({ key, label }) => (
              <button key={key} onClick={() => setFormData(p => ({ ...p, [key]: cycleStatus(p[key]) }))}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.42rem 0.65rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.84rem', ...statusButtonStyle(formData[key]) }}>
                <span>{label}</span>
                <StatusIcon value={formData[key]} small />
              </button>
            ))}
          </div>
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Notes</label>
          <textarea value={formData.notes || ''} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
            placeholder="e.g. Ask kitchen about cross-contamination risk on sesame"
            rows={3}
            style={{ width: '100%', padding: '0.5rem 0.7rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box', color: C.charcoal, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer', color: C.charcoal, marginBottom: '1.25rem' }}>
          <input type="checkbox" checked={formData.active} onChange={e => setFormData(p => ({ ...p, active: e.target.checked }))} />
          Active (visible to staff)
        </label>

        {!confirming ? (
          <button onClick={() => { if (formData.name) setConfirming(true); }} disabled={!formData.name}
            style={{ width: '100%', padding: '0.65rem', background: !formData.name ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem', cursor: !formData.name ? 'not-allowed' : 'pointer' }}>
            Save Dish
          </button>
        ) : (
          <div style={{ background: C.amberLight, border: `1px solid ${C.amber}`, borderRadius: '12px', padding: '1rem' }}>
            <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: C.charcoal, fontSize: '0.9rem' }}>
              Add <strong>{formData.name}</strong> to the allergen list?
            </p>
            <div style={{ display: 'flex', gap: '0.65rem' }}>
              <button onClick={handleConfirm} disabled={saving}
                style={{ flex: 1, padding: '0.6rem', background: C.amber, color: C.white, border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving...' : 'Confirm'}
              </button>
              <button onClick={() => setConfirming(false)}
                style={{ padding: '0.6rem 1rem', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', color: C.mid, fontSize: '0.88rem' }}>
                Go Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main component -----------------------------------------------------------
export default function Allergens({ currentUser }) {
  const isAdmin = ['admin', 'manager'].includes(currentUser?.role);

  const [dishes,          setDishes]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState('');
  const [activeAllergens, setActiveAllergens] = useState([]);
  const [subFilter,       setSubFilter]       = useState(false);
  const [menuFilter,      setMenuFilter]      = useState('All');
  const [showInactive,    setShowInactive]    = useState(false);
  const [showNuts,        setShowNuts]        = useState(true);
  const [selectedDish,    setSelectedDish]    = useState(null);
  const [showAddModal,    setShowAddModal]    = useState(false);

  const userVenue = currentUser?.venue;
  const allowedMenus = (() => {
    if (!userVenue) return ['Brunch', 'Dinner', 'Sassys'];
    if (userVenue === 'Sassys')       return ['Sassys'];
    if (userVenue === 'Box Jordaan')  return ['Brunch', 'Dinner'];
    if (userVenue === 'Box Plantage') return ['Brunch'];
    return ['Brunch', 'Dinner', 'Sassys'];
  })();

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase.from('allergen_list').select('*').order('name');
      if (!error && data) setDishes(data);
      setLoading(false);
    }
    load();
  }, []);

  const filtered = dishes.filter(d => {
    if (!allowedMenus.includes(d.menu))              return false;
    if (!showInactive && !d.active)                  return false;
    if (menuFilter !== 'All' && d.menu !== menuFilter) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
    for (const key of activeAllergens) {
      if (d[key] === 'no') return false;
    }
    if (subFilter && activeAllergens.length > 0) {
      const hasSub = activeAllergens.some(key => d[key] === 'sub' || d[key] === 'rem');
      if (!hasSub) return false;
    }
    return true;
  });

  function toggleAllergen(key) {
    setActiveAllergens(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  function handleDishSaved(updated) {
    setDishes(prev => prev.map(d => d.id === updated.id ? updated : d));
    setSelectedDish(updated);
  }

  function handleDishAdded(newDish) {
    setDishes(prev => [...prev, newDish].sort((a, b) => a.name.localeCompare(b.name)));
    setShowAddModal(false);
  }

  const visibleAllergens = ALLERGENS.filter(a => a.group === 'main' || (a.group === 'nut' && showNuts));

  return (
    <div style={{ padding: '1.25rem', maxWidth: '1400px', margin: '0 auto', background: C.cream, minHeight: '100vh' }}>

      {/* -- HEADER -- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.2rem', fontWeight: 700, color: C.charcoal }}>Allergens</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: C.mid }}>Always verify with kitchen before service</p>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: C.mid, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          )}
          {isAdmin && (
            <button onClick={() => setShowAddModal(true)}
              style={{ padding: '0.42rem 1rem', background: C.navy, color: C.white, border: `1.5px solid ${C.navy}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
              + Add Dish
            </button>
          )}
        </div>
      </div>

      {/* -- FILTERS -- */}
      <div style={{ background: C.white, borderRadius: '14px', padding: '1rem 1.1rem', marginBottom: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.75rem', alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search dish..."
            style={{ padding: '0.42rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '999px', fontSize: '0.85rem', width: '200px', outline: 'none', color: C.charcoal }} />

          {allowedMenus.length > 1 && (
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              {['All', ...allowedMenus].map(m => (
                <button key={m} onClick={() => setMenuFilter(m)}
                  style={{ padding: '0.32rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${menuFilter === m ? C.navy : C.border}`, background: menuFilter === m ? C.navy : C.white, color: menuFilter === m ? C.white : C.mid, fontSize: '0.82rem', cursor: 'pointer', fontWeight: menuFilter === m ? 600 : 400 }}>
                  {m}
                </button>
              ))}
            </div>
          )}

          <button onClick={() => setShowNuts(p => !p)}
            style={{ marginLeft: 'auto', padding: '0.32rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${showNuts ? C.orange : C.border}`, background: showNuts ? C.orangeLight : C.white, color: showNuts ? C.orange : C.mid, fontSize: '0.82rem', cursor: 'pointer', fontWeight: showNuts ? 600 : 400 }}>
            {showNuts ? 'Nuts visible' : 'Nuts hidden'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: '0.2rem' }}>Filter:</span>
          {ALLERGENS.filter(a => a.group === 'main' || showNuts).map(({ key, label }) => {
            const active = activeAllergens.includes(key);
            return (
              <button key={key} onClick={() => toggleAllergen(key)}
                style={{ padding: '0.25rem 0.7rem', borderRadius: '999px', border: `1.5px solid ${active ? C.red : C.border}`, background: active ? C.redLight : C.white, color: active ? C.red : C.mid, fontSize: '0.78rem', cursor: 'pointer', fontWeight: active ? 700 : 400, transition: 'all 0.12s' }}>
                {label}
              </button>
            );
          })}
          {activeAllergens.length > 0 && (
            <>
              <button onClick={() => setSubFilter(p => !p)}
                style={{ padding: '0.25rem 0.7rem', borderRadius: '999px', border: `1.5px solid ${subFilter ? C.skyText : C.border}`, background: subFilter ? C.skyBlueLight : C.white, color: subFilter ? C.skyText : C.mid, fontSize: '0.78rem', cursor: 'pointer', fontWeight: subFilter ? 700 : 400, transition: 'all 0.12s' }}>
                Removable only
              </button>
              <button onClick={() => { setActiveAllergens([]); setSubFilter(false); }}
                style={{ padding: '0.25rem 0.7rem', borderRadius: '999px', border: `1px solid ${C.border}`, background: C.cream, color: C.mid, fontSize: '0.78rem', cursor: 'pointer' }}>
                Clear X
              </button>
            </>
          )}
        </div>

        {activeAllergens.length > 0 && (
          <div style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: C.mid }}>
            Showing dishes containing: <strong style={{ color: C.charcoal }}>{activeAllergens.map(k => ALLERGENS.find(a => a.key === k)?.label).join(', ')}</strong>
            {subFilter && <span style={{ color: C.skyText, fontWeight: 600 }}> - removable only</span>}
            <span style={{ marginLeft: '0.5rem', color: C.mid }}>({filtered.length} dish{filtered.length !== 1 ? 'es' : ''})</span>
          </div>
        )}
      </div>

      {/* -- LEGEND -- */}
      <div style={{ display: 'flex', gap: '0.85rem', marginBottom: '0.85rem', flexWrap: 'wrap', alignItems: 'center', paddingLeft: '0.25rem' }}>
        {[
          { icon: <span style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:18,height:18,borderRadius:'50%',background:C.redLight,color:C.red,fontSize:'0.7rem',fontWeight:700 }}>X</span>, label: 'Contains' },
          { icon: <span style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:18,height:18,borderRadius:'50%',background:C.amberLight,color:C.amber,fontSize:'0.7rem' }}>~</span>, label: 'May contain' },
          { icon: <span style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:18,height:18,borderRadius:'50%',background:C.skyBlueLight,color:C.skyText,fontSize:'0.7rem',fontWeight:700 }}>Xs</span>, label: 'Can be subbed' },
          { icon: <span style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:18,height:18,borderRadius:'50%',background:C.oliveLight,color:C.olive,fontSize:'0.7rem',fontWeight:700 }}>Xr</span>, label: 'Can be removed' },
          { icon: <span style={{ color:C.border,fontSize:'0.9rem' }}>-</span>, label: 'Not present' },
          { icon: <span style={{ fontSize:'0.68rem',fontWeight:700,padding:'0.12rem 0.4rem',borderRadius:'999px',background:C.greenLight,color:C.green }}>VG</span>, label: 'Vegan' },
          { icon: <span style={{ fontSize:'0.68rem',fontWeight:700,padding:'0.12rem 0.4rem',borderRadius:'999px',background:C.skyBlueLight,color:C.skyText }}>V</span>, label: 'Vegetarian' },
          { icon: <span style={{ fontSize:'0.68rem',fontWeight:700,padding:'0.12rem 0.4rem',borderRadius:'999px',background:C.greenLight,color:C.green }}>VG*</span>, label: 'Vegan option' },
          { icon: <span style={{ fontSize:'0.68rem',fontWeight:700,padding:'0.12rem 0.4rem',borderRadius:'999px',background:C.skyBlueLight,color:C.skyText }}>V*</span>, label: 'Veg option' },
        ].map(({ icon, label }) => (
          <div key={label} style={{ display:'flex',alignItems:'center',gap:'0.35rem',fontSize:'0.75rem',color:C.mid }}>
            {icon}{label}
          </div>
        ))}
      </div>

      {/* -- TABLE -- */}
      {loading ? (
        <div style={{ background: C.white, borderRadius: '14px', padding: '2rem', textAlign: 'center', color: C.mid }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: C.white, borderRadius: '14px', padding: '2rem', textAlign: 'center', color: C.mid, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>No dishes found.</div>
      ) : (
        <div style={{ background: C.white, borderRadius: '14px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflowX: 'auto', border: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: '0.5rem 1rem', textAlign: 'left', position: 'sticky', left: 0, background: C.cream, minWidth: '180px' }} />
                <th style={{ padding: '0.5rem 0.5rem', minWidth: '50px' }} />
                <th
                  colSpan={ALLERGENS.filter(a => a.group === 'main').length}
                  style={{ padding: '0.4rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', borderRight: showNuts ? `2px solid ${C.border}` : 'none' }}>
                  14 EU Allergens
                </th>
                {showNuts && (
                  <th
                    colSpan={ALLERGENS.filter(a => a.group === 'nut').length}
                    style={{ padding: '0.4rem 0.5rem', fontSize: '0.65rem', fontWeight: 700, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                    Tree Nuts
                  </th>
                )}
              </tr>
              <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                <th style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.75rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', left: 0, background: C.white, minWidth: '180px' }}>
                  Dish
                </th>
                <th style={{ padding: '0.6rem 0.4rem', fontSize: '0.65rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'center', minWidth: '50px' }}>
                  Diet
                </th>
                {visibleAllergens.map(({ key, label, group }, idx) => {
                  const isActive   = activeAllergens.includes(key);
                  const isLastMain = group === 'main' && visibleAllergens[idx + 1]?.group === 'nut';
                  return (
                    <th key={key} onClick={() => toggleAllergen(key)}
                      style={{ padding: '0.6rem 0.3rem', fontSize: '0.65rem', color: isActive ? C.red : group === 'nut' ? C.orange : C.mid, fontWeight: isActive ? 700 : 600, textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap', background: isActive ? C.redLight : group === 'nut' ? `${C.orange}08` : 'transparent', borderRight: isLastMain ? `2px solid ${C.border}` : 'none', transition: 'all 0.12s' }}>
                      {label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.map((dish, rowIdx) => {
                const badge = dietaryBadge(dish.dietary);
                const rowBg = rowIdx % 2 === 0 ? C.white : C.cream;
                return (
                  <tr key={dish.id} style={{ borderBottom: rowIdx < filtered.length - 1 ? `1px solid ${C.border}` : 'none', opacity: dish.active ? 1 : 0.45, background: rowBg }}>
                    <td style={{ padding: '0.65rem 1rem', fontWeight: 600, position: 'sticky', left: 0, background: rowBg, minWidth: '180px', fontSize: '0.88rem' }}>
                      <span onClick={() => setSelectedDish(dish)}
                        style={{ color: C.navy, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: `${C.navy}44`, textUnderlineOffset: '2px' }}>
                        {dish.name}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>
                      {badge ? (
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.12rem 0.42rem', borderRadius: '999px', background: badge.bg, color: badge.color, whiteSpace: 'nowrap' }}>
                          {badge.label}
                        </span>
                      ) : <span style={{ color: C.border }}>-</span>}
                    </td>
                    {visibleAllergens.map(({ key, group }, idx) => {
                      const isActive   = activeAllergens.includes(key);
                      const isLastMain = group === 'main' && visibleAllergens[idx + 1]?.group === 'nut';
                      return (
                        <td key={key} style={{ padding: '0.5rem 0.3rem', textAlign: 'center', background: isActive ? `${C.red}08` : group === 'nut' ? `${C.orange}05` : 'transparent', borderRight: isLastMain ? `2px solid ${C.border}` : 'none' }}>
                          <StatusIcon value={dish[key]} small />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedDish && (
        <DishModal dish={selectedDish} isAdmin={isAdmin} onClose={() => setSelectedDish(null)} onSaved={handleDishSaved} />
      )}

      {showAddModal && isAdmin && (
        <AddDishModal allowedMenus={allowedMenus} onClose={() => setShowAddModal(false)} onSaved={handleDishAdded} />
      )}
    </div>
  );
}